import React, { useEffect, useCallback } from 'react';

import { CheckedState } from '@radix-ui/react-checkbox';
import moment from 'moment';
import styled from 'styled-components';

import { KEYS } from '../../../../../../constants/Common';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';

import { FadeIn } from '../../../Common/Animations/Keyframes';

// eslint-disable-next-line import/extensions
// @ts-ignore
import CheckBox from '../../../Common/Form/CheckBox';
import Tooltip from '../../../Common/Form/Tooltip';
import { KEYS_WHITELIST } from '../../Constants';
import { IAuthState } from '../../Store/Reducers';
import { IKeyDown, Types } from '../../Store/SignUpReducer';
import BirthDateSelector from './BirthData/BirthDateSelector/BirthDateSelector';
import CitySelector from './BirthData/CitySelector/CitySelector';
import TimeSelector from './BirthData/TimeSelector/TimeSelector';


const CB_ID = 'inform-birth-time-later';

interface IBirthDataProps {
    keyDown: IKeyDown,
    checkedWithoutHour: boolean,
}

type TBirthDataProps = IConnectedProps<IBirthDataProps>;

interface IToolTipSVG extends Omit<React.SVGProps<SVGSVGElement>, 'ref'> {
    version?: '1.1',
    viewBox?: '0 0 18 18',
    xmlns?: 'http://www.w3.org/2000/svg'
}

const Content = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    animation: ${FadeIn} .3s ease-out;
    gap: ${({ theme }) => theme.space.cat};

    & > u {
        cursor: pointer;
        font-size: 0.8125rem;
        margin-top: 0.8125rem;
        pointer-events: inherit;
        text-decoration: underline;
        transition: color .3s ease-out;
        color: ${({ theme }) => theme.colors.blue100};
    }
    
    & > p {
        width: 100%;
        font-size: 0.75em;
        text-align: center;
        line-height: normal;
        padding: 0 2.0938eem;
        margin: 0.8125em 0 0.9375rem;
        color: ${({ theme }) => theme.colors.blue200};
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.tablet.min}) {
        & > u {
            font-size: 1rem;
        }

        & > p {
            font-size: 0.9375rem;
            padding: 0 1.0938rem;
        }
    }
`;

const ToolTipSVG = styled.svg`
    width: 1.125rem;
    height: 1.125rem;
    margin: 0 0.3125em;
    vertical-align: middle;

    #info_tooltip {
        & > path {
            fill: ${({ theme }) => theme.colors.primary};
            stroke: ${({ theme }) => theme.colors.onPrimary};
        }

        & > g {
            fill: ${({ theme }) => theme.colors.onPrimary};
        }
    }
`;

const SVGToolTip: React.FC<IToolTipSVG> = (props) => (
    <ToolTipSVG xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 18 18" {...props}>
        <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g id="Astrolink/Styleguide/Icons-System" transform="translate(-316.000000, -260.000000)" fillRule="nonzero">
                <g id="info_tooltip" transform="translate(317.000000, 261.000000)">
                    <path d="M8,16 C3.581722,16 0,12.418278 0,8 C0,3.581722 3.581722,0 8,0 C12.418278,0 16,3.581722 16,8 C16,12.418278 12.418278,16 8,16 Z" id="Path" />
                    <g id="Group-5" transform="translate(5.248307, 4.018229)">
                        <path d="M1.59375,7.72851563 C1.59375,8.01757953 1.69824114,8.25488193 1.90722656,8.44042973 C2.11621198,8.62597753 2.37695156,8.71875003 2.68945313,8.71875003 C3.00195469,8.71875003 3.26269427,8.62597753 3.47167969,8.44042973 C3.68066511,8.25488193 3.78515625,8.01757953 3.78515625,7.72851563 C3.78515625,7.43945163 3.68066511,7.20214933 3.47167969,7.01660153 C3.26269427,6.83105373 3.00195469,6.73828123 2.68945313,6.73828123 C2.37695156,6.73828123 2.11621198,6.83105373 1.90722656,7.01660153 C1.69824114,7.20214933 1.59375,7.43945163 1.59375,7.72851563 Z" id="Path" />
                        <path d="M1.83398438,5.94726562 L3.50976563,5.94726562 C3.51757817,5.65038914 3.55859338,5.4052744 3.6328125,5.21191406 C3.70703162,5.01855372 3.83984279,4.83007904 4.03125,4.64648437 L4.70507813,4.01953125 C4.99023581,3.74218611 5.19726501,3.47070445 5.32617191,3.20507812 C5.45507881,2.93945179 5.51953121,2.64453287 5.51953121,2.3203125 C5.51953121,1.57812129 5.28027581,1.00586138 4.80175781,0.60351562 C4.3232398,0.20116986 3.64844186,2.66453526e-14 2.77734375,2.66453526e-14 C1.90233938,2.66453526e-14 1.22265867,0.21777126 0.73828125,0.65332031 C0.25390383,1.08886936 0.00781254,1.69726172 -2.43360887e-13,2.47851562 L1.98046875,2.47851562 C1.98828129,2.18554541 2.0644524,1.95312586 2.20898438,1.78125 C2.35351635,1.60937414 2.54296758,1.5234375 2.77734375,1.5234375 C3.28515879,1.5234375 3.5390625,1.81835642 3.5390625,2.40820312 C3.5390625,2.65039183 3.46386794,2.8720693 3.31347656,3.07324218 C3.16308519,3.27441507 2.94336082,3.49609254 2.65429688,3.73828125 C2.36523293,3.98046996 2.15625065,4.26659991 2.02734375,4.59667968 C1.89843686,4.92675946 1.83398438,5.37695027 1.83398438,5.94726562 Z" id="Path" />
                    </g>
                </g>
            </g>
        </g>
    </ToolTipSVG>
);

const BirthData: React.FC<TBirthDataProps> = ({ intl, dispatch, ...props }) => {
    const {
        keyDown,
        checkedWithoutHour
    } = props;

    const handleChangeCheckBox = useCallback((checked?: CheckedState) => {
        dispatch({ type: Types.CHANGE_WITHOUT_HOUR, data: checked });
    }, [dispatch]);

    useEffect(() => {
        const isCallbackIdActive = document.activeElement && document.activeElement.id !== `checkbox-${CB_ID}`;

        if (isCallbackIdActive || keyDown.key !== KEYS.ENTER) return;

        handleChangeCheckBox();
    }, [keyDown, handleChangeCheckBox]);

    useEffect(() => document.addEventListener('keydown', ({ key }) => {
        if (KEYS_WHITELIST.indexOf(key) === -1) return;

        dispatch({ type: Types.CHANGE_KEYDOW, data: { key, updatedAt: moment().toDate().getTime() } });
    }), [dispatch]);

    return (
        <Content data-cy="auth-birth-data-container">
            <CitySelector />
            <BirthDateSelector />
            <TimeSelector />

            <CheckBox
                id={CB_ID}
                tabIndex={0}
                inlineLabel
                checked={checkedWithoutHour}
                onCheckedChange={handleChangeCheckBox}
            >
                {intl.formatMessage({ id: 'authFunctional.birthData.birthTime' })}

                <Tooltip
                    text={intl.formatMessage({ id: 'authFunctional.birthData.toolTip' })}
                >
                    <SVGToolTip />
                </Tooltip>
            </CheckBox>
        </Content>
    );
};

const mapStateToProps: IAuthMapStateToProps<IBirthDataProps> = (state, isComparison = false) => ((!isComparison)
    ? {
        checkedWithoutHour: (state as IAuthState).SignUp.checkedWithoutHour,
        keyDown: (KEYS.ENTER === (state as IAuthState).SignUp.keyDown.key) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        keyDown: state.keyDown,
        checkedWithoutHour: state.checkedWithoutHour
    }
);

export default connectAuth<IBirthDataProps, null>(mapStateToProps)(BirthData);
